import { lazy } from 'react';
import { ModalMap } from 'react-easyrocket';
import { JackpotWinModal } from '../JackpotWinModal';
import { ConfirmBonusModal } from '../ConfirmBonusModal';
import { ReferralModal } from '../ReferralModal';

const AttentionModal = lazy(() => import('../AttentionBonus'));
const BackToSignupModal = lazy(() => import('../BackToSignupModal'));
const BonusModal = lazy(() => import('../BonusModal'));
const ChangeCurrencyModal = lazy(() => import('../ChangeCurrencyModal'));
const EventModal = lazy(() => import('src/modules/EventModal'));
const DepositCryptoModal = lazy(() => import('../DepositCryptoModal'));
const DepositModal = lazy(() => import('../DepositModal'));
const DepositPaymentInfoModal = lazy(() => import('../DepositPaymentInfoModal'));
const FreeSpinsModal = lazy(() => import('../FreeSpinsModal'));
const FullScreenImageModal = lazy(() => import('../FullScreenImageModal'));
const GameNotificationModal = lazy(() => import('../GameNotificationModal'));
const GameTournamentModal = lazy(() => import('../GameTournamentModal'));
const HellocleverResultModal = lazy(() => import('../HellocleverResult'));
const IosSetupModal = lazy(() => import('../IosSetupModal'));
const LatestDepositsModal = lazy(() => import('../LatestDepositsModal'));
const LoginModal = lazy(() => import('../LoginModal'));
const NewTournamentModal = lazy(() => import('../NewTournamentModal'));
const ResetPasswordModal = lazy(() => import('../ResetPasswordModal'));
const BuyFreespinsModal = lazy(() => import('../ShopEventModals/BuyFreespinsModal'));
const BuyMoneyModal = lazy(() => import('../ShopEventModals/BuyMoneyModal'));
const NoCoinsForFreespinsModal = lazy(() => import('../ShopEventModals/NoCoinsForFreespinsModal'));
const NoCoinsForMoneyModal = lazy(() => import('../ShopEventModals/NoCoinsForMoneyModal'));
const ShopModal = lazy(() => import('../ShopModal'));
const SignUpModal = lazy(() => import('../SignupModal'));
const TournamentMembersModal = lazy(() => import('../TournamentMembersModal'));
const VipRulesModal = lazy(() => import('../VipRulesModal'));
const ProfileRequirementsModal = lazy(() => import('../ProfileRequirementsModal'));
const GameNotAvailableModal = lazy(() => import('../GameNotAvailableModal'));
const LanguagesModal = lazy(() => import('../LanguagesModal'));
const SearchModal = lazy(() => import('../Search'));
const FormFields = lazy(() => import('../FormFields'));
const GameModal = lazy(() => import('src/modules/GPTGame/components/GameModal'));

export type ModalMapKeys =
  | 'BACK_TO_SIGNUP'
  | 'LOGIN'
  | 'SIGNUP'
  | 'SHOP'
  | 'DEPOSIT'
  | 'RESET_PASSWORD'
  | 'IOS_SETUP'
  | 'FORM_FIELDS'
  | 'DEPOSIT_CRYPTO'
  | 'BALANCE_CHANGE_CURRENCY'
  | 'ATTENTION_BONUS'
  | 'BONUS'
  | 'VIP_RULES'
  | 'DEPOSIT_PAYMENT_INFO'
  | 'NEW_TOURNAMENT'
  | 'GAME_NOTIFICATION'
  | 'FULLSCREEN_IMAGE'
  | 'DEPOSIT_HELLOCLEVER_RESULT'
  | 'LATEST_DEPOSIT'
  | 'FREESPINS'
  | 'GAME_TOURNAMENT'
  | 'TOURNAMENT_MEMBERS'
  | 'GPT_GAME_MODAL'
  | 'BUY_MONEY'
  | 'NO_COINS_FOR_MONEY'
  | 'BUY_FREESPINS'
  | 'NO_COINS_FOR_FREESPIN'
  | 'EVENT_MODAL'
  | 'PROFILE_REQUIREMENTS_MODAL'
  | 'GAME_NOT_AVAILABLE'
  | 'LANGUAGES'
  | 'SEARCH'
  | 'JACKPOT_WIN'
  | 'CONFIRM_BONUS_MODAL'
  | 'REFERRAL';

export const MODAL_MAP: ModalMap<ModalMapKeys> = {
  // Модалки, относящиеся к авторизации. Имеют самый высокий приоритет.
  LOGIN: {
    name: 'LOGIN',
    component: () => <LoginModal />,
    authOnly: false,
    priority: 1
  },
  SIGNUP: {
    name: 'SIGNUP',
    component: () => <SignUpModal />,
    authOnly: false,
    priority: 1
  },
  RESET_PASSWORD: {
    name: 'RESET_PASSWORD',
    component: () => <ResetPasswordModal />,
    authOnly: false,
    priority: 1
  },

  // Модалки, относящиеся к депозиту. Имеют самый высокий приоритет на равне с регистрацией.
  DEPOSIT: {
    name: 'DEPOSIT',
    component: () => <DepositModal />,
    authOnly: false,
    priority: 2
  },
  DEPOSIT_CRYPTO: {
    name: 'DEPOSIT_CRYPTO',
    component: () => <DepositCryptoModal />,
    authOnly: false,
    priority: 2
  },
  FORM_FIELDS: {
    name: 'FORM_FIELDS',
    component: () => <FormFields />,
    authOnly: false,
    priority: 2
  },
  LATEST_DEPOSIT: {
    name: 'LATEST_DEPOSIT',
    component: () => <LatestDepositsModal />,
    authOnly: false,
    priority: 3
  },
  DEPOSIT_HELLOCLEVER_RESULT: {
    name: 'DEPOSIT_HELLOCLEVER_RESULT',
    component: () => <HellocleverResultModal />,
    authOnly: false,
    priority: 3
  },
  DEPOSIT_PAYMENT_INFO: {
    name: 'DEPOSIT_PAYMENT_INFO',
    component: () => <DepositPaymentInfoModal />,
    authOnly: false,
    priority: 3
  },
  PROFILE_REQUIREMENTS_MODAL: {
    name: 'PROFILE_REQUIREMENTS_MODAL',
    component: () => <ProfileRequirementsModal />,
    authOnly: true,
    priority: 40
  },
  // Модалки, относящиеся к начислениям бонусов и фриспинов. Имеют достаточно высокий приоритет.
  BONUS: {
    name: 'BONUS',
    component: () => <BonusModal />,
    authOnly: false,
    priority: 1
  },
  CONFIRM_BONUS_MODAL: {
    name: 'CONFIRM_BONUS_MODAL',
    component: () => <ConfirmBonusModal />,
    authOnly: false,
    priority: 1
  },
  JACKPOT_WIN: {
    name: 'JACKPOT_WIN',
    component: () => <JackpotWinModal />,
    authOnly: true,
    priority: 10
  },
  FREESPINS: {
    name: 'FREESPINS',
    component: () => <FreeSpinsModal />,
    authOnly: false,
    priority: 20
  },

  // Модалки, относящиеся к изменению активной валюты пользователя. Имеют средний приоритет.
  BALANCE_CHANGE_CURRENCY: {
    name: 'BALANCE_CHANGE_CURRENCY',
    component: () => <ChangeCurrencyModal />,
    authOnly: false,
    priority: 50
  },
  ATTENTION_BONUS: {
    name: 'ATTENTION_BONUS',
    component: () => <AttentionModal />,
    authOnly: false,
    priority: 50
  },

  // Все остальные модалки. Имеют чуть ниже среднего - низкий приоритет.
  GPT_GAME_MODAL: {
    name: 'GPT_GAME_MODAL',
    component: () => <GameModal />,
    authOnly: false,
    priority: 60
  },
  NEW_TOURNAMENT: {
    name: 'NEW_TOURNAMENT',
    component: () => <NewTournamentModal />,
    authOnly: false,
    priority: 60
  },
  GAME_NOTIFICATION: {
    name: 'GAME_NOTIFICATION',
    component: () => <GameNotificationModal />,
    authOnly: false,
    priority: 60
  },
  FULLSCREEN_IMAGE: {
    name: 'FULLSCREEN_IMAGE',
    component: () => <FullScreenImageModal />,
    authOnly: false,
    priority: 100
  },
  BACK_TO_SIGNUP: {
    name: 'BACK_TO_SIGNUP',
    component: () => <BackToSignupModal />,
    authOnly: false,
    priority: 100
  },
  VIP_RULES: {
    name: 'VIP_RULES',
    component: () => <VipRulesModal />,
    authOnly: false,
    priority: 100
  },
  IOS_SETUP: {
    name: 'IOS_SETUP',
    component: () => <IosSetupModal />,
    authOnly: false,
    priority: 100
  },
  SHOP: {
    name: 'SHOP',
    component: () => <ShopModal />,
    authOnly: true,
    priority: 100
  },
  GAME_TOURNAMENT: {
    name: 'GAME_TOURNAMENT',
    component: () => <GameTournamentModal />,
    authOnly: true,
    priority: 100
  },
  TOURNAMENT_MEMBERS: {
    name: 'TOURNAMENT_MEMBERS',
    component: () => <TournamentMembersModal />,
    authOnly: true,
    priority: 100
  },
  BUY_MONEY: {
    name: 'BUY_MONEY',
    component: () => <BuyMoneyModal />,
    authOnly: true,
    priority: 100
  },
  NO_COINS_FOR_MONEY: {
    name: 'NO_COINS_FOR_MONEY',
    component: () => <NoCoinsForMoneyModal />,
    authOnly: true,
    priority: 100
  },
  BUY_FREESPINS: {
    name: 'BUY_FREESPINS',
    component: () => <BuyFreespinsModal />,
    authOnly: true,
    priority: 100
  },
  NO_COINS_FOR_FREESPIN: {
    name: 'NO_COINS_FOR_FREESPIN',
    component: () => <NoCoinsForFreespinsModal />,
    authOnly: true,
    priority: 100
  },
  EVENT_MODAL: {
    name: 'EVENT_MODAL',
    component: () => <EventModal />,
    authOnly: true,
    priority: 100
  },
  GAME_NOT_AVAILABLE: {
    name: 'GAME_NOT_AVAILABLE',
    component: () => <GameNotAvailableModal />,
    authOnly: false,
    priority: 110
  },
  LANGUAGES: {
    name: 'LANGUAGES',
    component: () => <LanguagesModal />,
    authOnly: false,
    priority: 100
  },
  SEARCH: {
    name: 'SEARCH',
    component: () => <SearchModal />,
    authOnly: false,
    priority: 100
  },
  REFERRAL: {
    name: 'REFERRAL',
    component: () => <ReferralModal />,
    authOnly: false,
    priority: 100
  }
};
